import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { Colors, IColors } from '../styles/theme';
type Theme = 'dark' | 'darker';

const THEME_STORAGE_KEY = 'bundle-drop-theme';

interface ThemeContextProps {
  theme: Theme;
  toggleTheme: () => void;
  setTheme: (theme: Theme) => void;
  colors: IColors;
}

const ThemeContext = createContext<ThemeContextProps | undefined>(undefined);

export const ThemeProvider = ({ children }: { children: ReactNode }) => {
  const [theme, setThemeState] = useState<Theme>(() => {
    if (typeof window === 'undefined') {
      return 'darker';
    }
    const savedTheme = window.localStorage.getItem(THEME_STORAGE_KEY);
    return savedTheme === 'dark' || savedTheme === 'darker' ? savedTheme : 'darker';
  });
  const [colors, setColors] = useState<IColors>(Colors[theme]);

  useEffect(() => {
    setColors(Colors[theme]);
  }, [theme]);

  const toggleTheme = () => {
    setThemeState(prev => (prev === 'dark' ? 'darker' : 'dark'));
  };

  const setTheme = (nextTheme: Theme) => {
    setThemeState(nextTheme);
  };

  useEffect(() => {
    if (typeof document === 'undefined') {
      return;
    }
    const root = document.documentElement;
    root.dataset.theme = theme;
    window.localStorage.setItem(THEME_STORAGE_KEY, theme);
    root.style.setProperty('--app-bg-400', colors.bg_400 ?? '');
    root.style.setProperty('--app-bg-500', colors.bg_500 ?? '');
    root.style.setProperty('--app-bg-600', colors.bg_600 ?? '');
    root.style.setProperty('--app-primary', colors.primary ?? '');
    root.style.setProperty('--app-accent', colors.accent ?? '');
    root.style.setProperty('--app-text-400', colors.text_400 ?? '');
    root.style.setProperty('--app-text-500', colors.text_500 ?? '');
    root.style.setProperty('--app-input-bg', colors.input_background ?? '');
    root.style.setProperty('--app-input-border', colors.input_border ?? '');
    root.style.setProperty('--app-input-border-focus', colors.input_border_focus ?? '');
    root.style.setProperty('--app-input-text', colors.input_text ?? '');
    root.style.setProperty('--app-input-placeholder', colors.input_placeholder ?? '');
    root.style.setProperty('--app-error-red', colors.error_red ?? '');
  }, [theme, colors]);

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme, setTheme, colors }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = (): ThemeContextProps => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};
